import type {FilterObject} from 'nutshell-core/types';

import type {VersionParam} from '../../../reports/report-types';

import {EDITION_FILTER_KEYS} from '../../utils';

export const editionFieldFilterFunction = (fieldName: string) => {
    // @ts-expect-error
    const editionFieldKeys = EDITION_FILTER_KEYS.concat(['period']);

    // @ts-expect-error
    return !editionFieldKeys.includes(fieldName) && !fieldName.startsWith('clickedEditionUrls');
};

export const getUpdatedFilters = (
    option: {
        value: FilterObject;
        label: string;
    },
    filters: FilterObject[],
    editionId: string
) => {
    const baseFilters: FilterObject[] = filters.filter((filterObj) => {
        const filterKey = Object.keys(filterObj)[0];
        // Base filters include filters that aren't related to editions array i.e. {jobTitle: 'manager'}
        // AND aren't clickedEditionUrls filters

        return (
            // @ts-expect-error
            (editionFieldFilterFunction(filterKey) ||
                // Base filters include period and sentEditions filters
                Object.prototype.hasOwnProperty.call(filterObj, 'period') ||
                Object.prototype.hasOwnProperty.call(filterObj, 'sentEditions')) &&
            // Exclude any clickedEditionUrls filters from base
            // @ts-expect-error
            !filterKey.startsWith('clickedEditionUrls')
        );
    });

    // If this is a link filter (clickedEditionUrls), we need to preserve the clickedEditions filter
    // @ts-expect-error
    const isLinkFilter = Object.keys(option.value)[0].startsWith('clickedEditionUrls');
    if (isLinkFilter) {
        const clickedFilter = filters.find((f) =>
            Object.prototype.hasOwnProperty.call(f, 'clickedEditions')
        );
        if (clickedFilter) {
            baseFilters.push(clickedFilter);
        }
    }

    let newFilters = baseFilters.concat(option.value);

    // To remove extra filters and show 'All recipients,' we just save the period filter
    // plus the sentEditions filter and any that aren't related to editions array i.e. {jobTitle: 'manager'}
    if (option.value.sentEditions) {
        const periodFilter: FilterObject[] = filters.filter(
            (filterObj) =>
                // @ts-expect-error
                editionFieldFilterFunction(Object.keys(filterObj)[0]) ||
                Object.prototype.hasOwnProperty.call(filterObj, 'period')
        );
        newFilters = periodFilter.concat(option.value);
    }

    // We save the notViewedEditions filter as a negative viewedEditions filter,
    // also including negative unsubscribedEditions and negative bouncedEditions
    // filters, resulting in a concise list of people to resend the edition to
    if (option.value.notViewedEditions) {
        newFilters = baseFilters.concat(
            {
                viewedEditions: {
                    anyAll: 'any',
                    none: false,
                    data: [{isNot: true, data: editionId}],
                },
            },
            {
                unsubscribedEditions: {
                    anyAll: 'any',
                    none: false,
                    data: [{isNot: true, data: editionId}],
                },
            },
            {
                bouncedEditions: {
                    anyAll: 'any',
                    none: false,
                    data: [{isNot: true, data: editionId}],
                },
            }
        );
    }

    return newFilters;
};

// If viewing an A/B broadcast with a comparison edition, the user can view recipients for the A version, the B version, or both versions.
// This function adjusts the filters based on the selected versions viewed.
export const adjustFiltersForComparisonEdition = (
    filters: FilterObject[],
    comparisonEditionId: string,
    version?: VersionParam
): FilterObject[] => {
    const versionParam = version ? version.toLowerCase() : undefined;
    if (versionParam === 'a') {
        // Hack to allow filtering by version recipients in the recipients tab while being able to see all recipients in the people list page
        // @ts-expect-error
        if (Object.keys(filters[1])[0].includes('Editions') && filters.length < 3) {
            // @ts-expect-error
            filters.push(filters[1]);
        }

        // If we have a single sentEditions filter, duplicate it
        // also a hack solution for the A-version clicked and opened filters showing recipients from both versions
        if (
            // @ts-expect-error
            Object.keys(filters[1])[0] === 'sentEditions' &&
            // @ts-expect-error
            Object.keys(filters[2])[0] !== 'sentEditions'
        ) {
            // @ts-expect-error
            filters.push(filters[1]);
        }

        return filters;
    } else if (versionParam === 'b') {
        // remove existing editionId filters
        // add comparisonEditionId filters
        const newFilters: FilterObject[] = filters.map((filterObject) => {
            const filterKey = Object.keys(filterObject)[0];
            // @ts-expect-error
            const filterValue = filterObject[filterKey];

            // @ts-expect-error
            if (typeof filterValue === 'string' && filterKey.includes('Editions')) {
                // @ts-expect-error
                filterObject[filterKey] = {
                    anyAll: 'any',
                    data: [
                        {
                            data: comparisonEditionId,
                            isNot: false,
                        },
                    ],
                };
                // @ts-expect-error
            } else if (typeof filterValue === 'object' && filterKey.includes('Editions')) {
                // @ts-expect-error
                filterObject[filterKey] = {
                    ...filterValue,
                    data: [
                        ...filterValue.data,
                        {...filterValue.data[0], data: comparisonEditionId},
                    ],
                };
            }

            return filterObject;
        });

        return newFilters;
    } else {
        const comparisonFilters: FilterObject[] = filters.map((filterObject) => {
            const filterKey = Object.keys(filterObject)[0];
            // @ts-expect-error
            const filterValue = filterObject[filterKey];

            // @ts-expect-error
            if (typeof filterValue === 'string' && filterKey.includes('Editions')) {
                // @ts-expect-error
                filterObject[filterKey] = {
                    anyAll: 'any',
                    data: [
                        {
                            data: filterValue,
                            isNot: false,
                        },
                        {
                            data: comparisonEditionId,
                            isNot: false,
                        },
                    ],
                };
                // @ts-expect-error
            } else if (typeof filterValue === 'object' && filterKey.includes('Editions')) {
                // @ts-expect-error
                filterObject[filterKey] = {
                    ...filterValue,
                    data: [
                        ...filterValue.data,
                        {...filterValue.data[0], data: comparisonEditionId},
                    ],
                };
            }

            return filterObject;
        });

        return comparisonFilters;
    }
};

export const getEditionIdFromApiId = (apiId: string) => {
    return apiId.split('-')[0];
};

export const adjuctClickedEditionsFilterOnVersionChange = (
    currentFilters: FilterObject,
    optionValue: string,
    comparisonEditionId: string,
    editionId: string
): {
    [key: string]: any;
} => {
    // Create new filters object without clickedEditionUrls from previous version
    // @ts-expect-error
    const cleanedFilters = Object.fromEntries<string, any>(
        (Object.entries(currentFilters) as Array<[string, any]>).filter(
            // @ts-expect-error
            ([key]: [any]) => !key.startsWith('clickedEditionUrls')
        )
    );

    // Update clickedEditions filter if it exists from previous version editionId
    if (cleanedFilters.clickedEditions) {
        const targetId = optionValue === 'b' ? comparisonEditionId : editionId;
        if (typeof cleanedFilters.clickedEditions === 'string') {
            cleanedFilters.clickedEditions = targetId;
        } else if (cleanedFilters.clickedEditions.data) {
            // Handle case where clickedEditions is an object with data array
            cleanedFilters.clickedEditions = {
                ...cleanedFilters.clickedEditions,
                data: Array.isArray(cleanedFilters.clickedEditions.data)
                    ? // @ts-expect-error
                      cleanedFilters.clickedEditions.data.map((item) => ({
                          ...item,
                          data: targetId,
                      }))
                    : [],
            };
        }
    }

    return cleanedFilters;
};
